import { useLocation, Link, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Phone, Stethoscope, ListChecks, UserRound } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CtaBand } from "@/components/common/CtaBand";
import { BeforeAfterGallery } from "@/components/common/BeforeAfterGallery";
import { FaqSection } from "@/components/home/FaqSection";
import { Seo } from "@/components/common/Seo";
import { MedicalSources } from "@/components/common/MedicalSources";
import { buildServiceSchema, buildBreadcrumbSchema, buildFaqSchema } from "@/lib/schema";
import { ALL_TREATMENTS, ALL_TREATMENT_CONTENT, CONTACT } from "@/data/site";
import { ToothIcon } from "@/components/common/ToothIcon";
import { CtaPair } from "@/components/common/CtaButtons";

export default function TreatmentDetail() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const t = ALL_TREATMENTS.find((x) => x.slug === slug);
  if (!t) return <Navigate to="/treatments" replace />;

  const c = ALL_TREATMENT_CONTENT[slug] || {};
  const faqs = c.faqs || [];
  const category = t.category || "dental";
  const isAesthetic = category === "skin" || category === "hair";
  const listPath = isAesthetic ? "/aesthetic-treatments" : "/treatments";
  const listLabel = isAesthetic ? "Aesthetic Treatments" : "Treatments";
  const eyebrow = category === "skin" ? "Skin Treatment" : category === "hair" ? "Hair Treatment" : "Dental Treatment";
  const specialist = isAesthetic ? "skin & hair specialist" : "dentist";
  const related = ALL_TREATMENTS.filter((x) => x.slug !== slug && (x.category || "dental") === category).slice(0, 3);

  return (
    <div data-testid="treatment-detail-page">
      <Seo
        title={`${t.name} in Varanasi | Dr. Ankesh Seth`}
        description={`${t.short} Consult Dr. Ankesh Seth, trusted ${specialist} in Varanasi, for safe and effective ${t.name.toLowerCase()}. Call or WhatsApp to book.`}
        path={`/${t.slug}`}
        jsonLd={[
          buildServiceSchema(t, c.surgeon?.name),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: listLabel, path: listPath },
            { name: t.name, path: `/${t.slug}` },
          ]),
          ...(faqs.length ? [buildFaqSchema(faqs)] : []),
        ]}
      />
      <PageHero
        eyebrow={eyebrow}
        titleLines={[t.name]}
        subtitle={t.short}
        crumbs={[{ label: listLabel, to: listPath }, { label: t.name }]}
      />

      <section className="bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-6 py-24 md:px-12 md:py-28 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16 lg:px-20">
          <Reveal>
            <div className="aspect-[16/10] overflow-hidden rounded-[2rem]">
              <img src={t.image} alt={`${t.name} treatment at Dr. Ankesh Seth's clinic in Varanasi`} width="800" height="500" loading="eager" fetchPriority="high" className="h-full w-full object-cover" />
            </div>
            <div className="mt-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                <ToothIcon className="h-6 w-6" />
              </span>
              <h2 className="mt-5 font-display text-3xl font-semibold text-brand-dark md:text-4xl">
                What is {t.name}?
              </h2>
              {(c.intro || [t.overview]).map((para, i) => (
                <p key={i} className="mt-5 text-lg leading-relaxed text-brand-muted">{para}</p>
              ))}

              {c.surgeon && (
                <div className="mt-8 flex items-start gap-4 rounded-2xl border border-brand-blue/20 bg-brand-blue/[0.04] p-6" data-testid="treatment-surgeon-callout">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                    <UserRound className="h-6 w-6" />
                  </span>
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-blue">Performed by</span>
                    <p className="mt-1 font-display text-xl font-semibold text-brand-dark" data-testid="treatment-surgeon-name">{c.surgeon.name}</p>
                    <p className="text-sm font-medium text-brand-green">{c.surgeon.role}</p>
                    {c.surgeon.note && <p className="mt-2 text-sm leading-relaxed text-brand-muted">{c.surgeon.note}</p>}
                  </div>
                </div>
              )}

              {c.signs && (
                <>
                  <h3 className="mt-12 flex items-center gap-2 font-display text-2xl font-medium text-brand-dark">
                    <Stethoscope className="h-6 w-6 text-brand-blue" />
                    Signs you may need {t.name.toLowerCase()}
                  </h3>
                  <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                    {c.signs.map((s) => (
                      <li key={s} className="flex items-start gap-3 rounded-xl border border-brand-line bg-white p-4">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-blue" />
                        <span className="text-sm text-brand-dark">{s}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <h3 className="mt-12 flex items-center gap-2 font-display text-2xl font-medium text-brand-dark">
                <CheckCircle2 className="h-6 w-6 text-brand-green" />
                Key benefits
              </h3>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {t.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 rounded-xl border border-brand-line bg-brand-soft p-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />
                    <span className="text-sm text-brand-dark">{p}</span>
                  </li>
                ))}
              </ul>

              {c.procedure && (
                <>
                  <h3 className="mt-12 flex items-center gap-2 font-display text-2xl font-medium text-brand-dark">
                    <ListChecks className="h-6 w-6 text-brand-blue" />
                    What to expect — step by step
                  </h3>
                  <ol className="mt-5 space-y-4">
                    {c.procedure.map((step, i) => (
                      <li key={step.title} className="flex gap-4 rounded-xl border border-brand-line bg-white p-5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-blue text-sm font-semibold text-white">
                          {i + 1}
                        </span>
                        <div>
                          <p className="font-medium text-brand-dark">{step.title}</p>
                          <p className="mt-1 text-sm text-brand-muted">{step.text}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </>
              )}

              {c.whyUs && (
                <>
                  <h3 className="mt-12 font-display text-2xl font-medium text-brand-dark">
                    Why choose {c.surgeon ? c.surgeon.name : "Dr. Ankesh Seth"} in Varanasi
                  </h3>
                  <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                    {c.whyUs.map((w) => (
                      <li key={w} className="flex items-start gap-3 rounded-xl border border-brand-line bg-brand-soft p-4">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />
                        <span className="text-sm text-brand-dark">{w}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <div className="mt-10 rounded-2xl border border-brand-blue/20 bg-brand-blue/[0.04] p-6">
                <h3 className="font-display text-xl font-medium text-brand-dark">Transparent, honest pricing</h3>
                <p className="mt-2 text-sm text-brand-muted">
                  The cost of {t.name.toLowerCase()} depends on your specific case. Dr. Ankesh Seth
                  always shares a clear treatment plan with full pricing before starting — no hidden
                  charges, no surprises. Call or WhatsApp for an honest, personalised estimate.
                </p>
              </div>

              <MedicalSources sources={c.sources} />
            </div>
          </Reveal>

          {/* Sticky booking card */}
          <Reveal delay={0.1}>
            <div className="lg:sticky lg:top-28">
              <div className="rounded-[2rem] border border-brand-line bg-brand-soft p-8">
                <h3 className="font-display text-2xl font-medium text-brand-dark">
                  Book your {t.name}
                </h3>
                <p className="mt-2 text-sm text-brand-muted">
                  Speak with {c.surgeon ? `${c.surgeon.name}'s` : "Dr. Ankesh Seth's"} team to discuss your treatment and get a transparent
                  plan — no obligation.
                </p>
                <div className="mt-6 flex flex-col gap-3 [&_a]:w-full [&_a]:justify-center">
                  <CtaPair className="!flex-col !items-stretch" />
                </div>
                <div className="mt-6 border-t border-brand-line pt-6 text-sm text-brand-muted">
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-brand-blue" />
                    {CONTACT.phoneDisplay}
                  </p>
                  <p className="mt-2">{CONTACT.address}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-soft">
        <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-12 lg:px-20">
          <Reveal className="mb-10">
            <h2 className="font-display text-3xl font-semibold text-brand-dark md:text-4xl">
              Related treatments
            </h2>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-3">
            {related.map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.08}>
                <Link
                  to={`/${r.slug}`}
                  className="group flex items-center justify-between rounded-2xl border border-brand-line bg-white p-6 transition-all hover:-translate-y-1 hover:border-brand-blue/40"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue">
                      <ToothIcon className="h-5 w-5" />
                    </span>
                    <span className="font-medium text-brand-dark">{r.name}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-brand-blue transition-transform group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FaqSection faqs={faqs.length ? faqs : undefined} />
      {!isAesthetic && <BeforeAfterGallery muted />}
      <CtaBand title={`Ready for your ${t.name}?`} />
    </div>
  );
}
